

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import Snackbar from '../Components/Snackbar';
import ConfirmDialog from '../Components/ConfirmDialog';

const Request = ({ onClose, onRequestSubmit }) => {
  // State management
  const [formData, setFormData] = useState({
    department: '',
    requestTo: '',
    subject: '',
    description: ''
  });

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // File attachment states
  const [attachments, setAttachments] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Refs for file inputs and audio handling
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info'
  });

  // Confirm Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null
  });

  const [dialogLoading, setDialogLoading] = useState(false);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);

  // API configuration
  const API_BASE_URL = 'https://slateblue-mallard-430836.hostingersite.com/api';
  const authToken = localStorage.getItem('authToken')?.replace(/['\"]/g, '').trim();

  // Enhanced getCompanyId function
  const getCompanyId = useCallback(async () => {
    console.group('[DEBUG] getCompanyId() - Starting company ID resolution');

    const token = localStorage.getItem('authToken');
    console.log('📋 Token from localStorage:', token ? `${token.substring(0, 50)}...` : 'null');

    if (token) {
      try {
        console.log(' Attempting to decode JWT token...');
        const decodedToken = jwtDecode(token);
        console.log('📊 Decoded token structure:', JSON.stringify(decodedToken, null, 2));

        if (decodedToken) {
          console.log('Available token fields:', Object.keys(decodedToken));
          console.log('Token companyId field:', decodedToken.companyId);
          console.log('Token userId field:', decodedToken.userId);
          console.log('†Token id field:', decodedToken.id);
          console.log('Ž­ Token role field:', decodedToken.role);
        }

        // If role is company, use id as companyId
        if (decodedToken && decodedToken.role === 'company') {
          if (decodedToken.id) {
            console.log('✅ Role is company, using id as companyId:', decodedToken.id);
            console.groupEnd();
            return decodedToken.id;
          }
        }

        // Otherwise, use companyId field if available
        if (decodedToken && decodedToken.companyId) {
          console.log('✅ Using companyId from token:', decodedToken.companyId);
          console.groupEnd();
          return decodedToken.companyId;
        }

        console.warn('⚠️ No companyId found in token');

      } catch (error) {
        console.error('❌ JWT decoding failed:', error);
        console.warn('„ Trying alternative methods...');
      }
    } else {
      console.warn('⚠️ No authToken found in localStorage');
      console.log('📋 All localStorage keys:', Object.keys(localStorage));
    }

    // Fallback: try localStorage
    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId) {
      console.log('✅ Found companyId in localStorage:', storedCompanyId);
      console.groupEnd();
      return storedCompanyId;
    } else {
      console.warn('⚠️ No companyId found in localStorage');
    }

    console.error('❌ Unable to resolve company ID from any source');
    console.groupEnd();
    return null;
  }, []);

  // Updated getCurrentUserId function
  const getCurrentUserId = useCallback(() => {
    console.group('[DEBUG] getCurrentUserId()');

    let userId = null;
    let foundKey = null;

    // 1. Prioritize extracting user ID directly from JWT authToken (Source of Truth)
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (token) {
        const decoded = jwtDecode(token);
        let jwtUserId = decoded.userId || decoded.id || decoded.employeeId || decoded.user?.id || decoded._id;
        if (jwtUserId && typeof jwtUserId === 'object') {
          jwtUserId = jwtUserId._id || jwtUserId.id;
        }
        if (jwtUserId) {
          userId = String(jwtUserId).replace(/['"]/g, '').trim();
          foundKey = 'JWT';
        }
      }
    } catch (error) {
      console.error('❌ Failed to extract userId from JWT:', error);
    }

    // 2. Fallback to localStorage keys only if JWT token is missing/invalid
    if (!userId) {
      const possibleKeys = ['userId', 'user_id', 'id', 'employeeId', 'employee_id'];
      for (const key of possibleKeys) {
        const value = localStorage.getItem(key);
        if (value) {
          userId = String(value).replace(/['"]/g, '').trim();
          foundKey = key;
          console.log(`✅ Found userId with key '${key}':`, userId);
          break;
        }
      }
    }

    if (!userId) {
      console.warn('⚠️ No user ID found in localStorage or JWT');
    } else {
      console.log('🎯 Using userId from:', foundKey);
    }

    console.groupEnd();
    return userId;
  }, []);

  // Form validation function
  const validateForm = useCallback(() => {
    if (!formData.department) {
      return { isValid: false, error: 'Please select a department' };
    }
    if (!formData.requestTo) {
      return { isValid: false, error: 'Please select an employee' };
    }
    if (!formData.subject.trim()) {
      return { isValid: false, error: 'Please enter a subject' };
    }
    if (!formData.description.trim()) {
      return { isValid: false, error: 'Please enter a description' };
    }
    return { isValid: true, error: null };
  }, [formData.department, formData.requestTo, formData.subject, formData.description]);

  // FIXED: Enhanced fetch departments function
  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      console.group('[DEBUG] fetchDepartments() - Enhanced version');

      const companyId = await getCompanyId();
      const token = authToken;

      console.log('Department fetch data:', {
        companyId,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
      });

      if (!companyId || !token) {
        throw new Error('Missing company ID or authentication token');
      }

      const url = `${API_BASE_URL}/departments/company/${companyId}`;
      console.log('Œ Fetching departments from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        credentials: 'include'
      });

      console.log('¥ Departments response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Departments error response:', errorText);
        throw new Error(`Failed to fetch departments (${response.status})`);
      }

      const data = await response.json();
      console.log('📊 Departments raw response:', JSON.stringify(data, null, 2));

      const deptList = Array.isArray(data) ? data : (data.data || data.departments || []);
      console.log('📦 Processed departments list:', deptList.length, 'departments');

      setDepartments(deptList);
      console.log('✅ Departments loaded successfully:', deptList.length);
      console.groupEnd();

    } catch (error) {
      console.error('❌ Error fetching departments:', error);
      setError('Failed to load departments');
      if (window.toast) {
        window.toast.error('Failed to load departments: ' + error.message);
      }
      console.groupEnd();
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, authToken, getCompanyId]);

  // FIXED: Enhanced fetch all employees function
  const fetchAllEmployees = useCallback(async () => {
    try {
      console.group('[DEBUG] fetchAllEmployees() - Enhanced version');

      const companyId = await getCompanyId();
      const token = authToken;

      console.log('Employee fetch data:', {
        companyId,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
      });

      if (!companyId || !token) {
        console.warn('⚠️ Missing company ID or token for employees fetch');
        console.groupEnd();
        return;
      }

      const url = `${API_BASE_URL}/employees/company/${companyId}`;
      console.log('Œ Fetching employees from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        credentials: 'include'
      });

      console.log('¥ Employees response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Employees raw response:', JSON.stringify(data, null, 2));

        const empList = Array.isArray(data) ? data : (data.data || data.employees || []);
        console.log('📦 Processed employees list:', empList.length, 'employees');

        // Enhanced employee logging with department analysis
        if (empList.length > 0) {
          console.log('Sample employee structure:', JSON.stringify(empList[0], null, 2));
          console.log('All employee department structures:');
          empList.forEach((emp, index) => {
            console.log(`Employee ${index + 1} (${getEmployeeName(emp)}):`, {
              id: emp._id,
              name: getEmployeeName(emp),
              department: emp.department,
              departments: emp.departments,
              departmentRef: emp.departmentRef,
              dept: emp.dept,
              deptId: emp.deptId,
              // Check all possible department fields
              allFields: Object.keys(emp).filter(key => key.toLowerCase().includes('dept'))
            });
          });
        }

        setEmployees(empList);
        console.log('✅ Employees loaded successfully:', empList.length);
      } else {
        const errorText = await response.text();
        console.error('❌ Employees fetch failed:', response.status, errorText);
        if (window.toast) {
          window.toast.error('Failed to load employees');
        }
      }

      console.groupEnd();
    } catch (error) {
      console.error('❌ Error fetching employees:', error);
      if (window.toast) {
        window.toast.error('Failed to load employees: ' + error.message);
      }
      console.groupEnd();
    }
  }, [API_BASE_URL, authToken, getCompanyId]);

  // Fetch data on component mount
  useEffect(() => {
    const loadData = async () => {
      console.log('🚀 Starting data load sequence...');

      await fetchDepartments();
      await fetchAllEmployees();

      console.log('✅ Data loading sequence complete');
    };

    loadData();
  }, [fetchDepartments, fetchAllEmployees]);

  // COMPLETELY REVISED: Universal employee filtering function
  const filterEmployeesByDepartment = useCallback((employees, selectedDepartmentId) => {
    if (!selectedDepartmentId || !employees.length) {
      return [];
    }

    console.group('[DEBUG] Universal Employee Filtering');
    console.log('📋 Input data:', {
      selectedDepartmentId,
      totalEmployees: employees.length
    });

    const selectedDeptStr = String(selectedDepartmentId).trim();

    const filtered = employees.filter(emp => {
      console.log(`\nChecking employee: ${getEmployeeName(emp)} (${emp._id})`);

      // Get all possible department-related values from the employee object
      const departmentValues = [];

      // Check all possible department field variations
      const departmentFields = [
        'department', 'departments', 'departmentRef', 'dept', 'deptId',
        'departmentId', 'departmentIds', 'assignedDepartments', 'workDepartments'
      ];

      departmentFields.forEach(field => {
        const value = emp[field];
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Handle arrays
            value.forEach(item => {
              if (typeof item === 'object' && item !== null) {
                // Object in array - get _id or id
                if (item._id) departmentValues.push(String(item._id));
                if (item.id) departmentValues.push(String(item.id));
              } else if (item) {
                // Primitive in array
                departmentValues.push(String(item));
              }
            });
          } else if (typeof value === 'object' && value !== null) {
            // Single object - get _id or id
            if (value._id) departmentValues.push(String(value._id));
            if (value.id) departmentValues.push(String(value.id));
          } else {
            // Primitive value
            departmentValues.push(String(value));
          }
        }
      });

      // Remove duplicates
      const uniqueValues = [...new Set(departmentValues)];

      console.log('Department values found:', uniqueValues);

      // Check if any department value matches
      const isMatch = uniqueValues.some(value => value === selectedDeptStr);

      console.log(`✅ Match result: ${isMatch}`);

      return isMatch;
    });

    console.log('📊 Filtering results:', {
      totalEmployees: employees.length,
      filteredCount: filtered.length,
      filteredEmployees: filtered.map(emp => ({
        id: emp._id,
        name: getEmployeeName(emp)
      }))
    });

    console.groupEnd();
    return filtered;
  }, []);

  // SIMPLIFIED: Employee filtering effect
  useEffect(() => {
    console.group('„ Employee filtering effect triggered');
    console.log('📋 Effect inputs:', {
      selectedDepartment: formData.department,
      totalEmployees: employees.length
    });

    if (formData.department && employees.length > 0) {
      const filtered = filterEmployeesByDepartment(employees, formData.department);
      setFilteredEmployees(filtered);

      // Reset selected employee if not in filtered list
      if (formData.requestTo && !filtered.find(emp => String(emp._id) === String(formData.requestTo))) {
        console.log('„ Resetting selected employee (not in filtered list)');
        setFormData(prev => ({ ...prev, requestTo: '' }));
      }
    } else {
      console.log('❌ No department selected or no employees available');
      setFilteredEmployees([]);
      if (formData.requestTo) {
        console.log('„ Clearing selected employee');
        setFormData(prev => ({ ...prev, requestTo: '' }));
      }
    }

    console.groupEnd();
  }, [formData.department, formData.requestTo, employees, filterEmployeesByDepartment]);

  // Event handlers
  const handleInputChange = useCallback((field, value) => {
    console.log(`„ Input change: ${field} = ${value}`);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  }, []);

  // const handleFileUpload = useCallback((e, type) => {
  //   const files = Array.from(e.target.files);
  //   const newAttachments = files.map(file => ({
  //     id: Date.now() + Math.random(),
  //     name: file.name,
  //     type: type,
  //     size: file.size,
  //     file: file,
  //     url: URL.createObjectURL(file),
  //     uploadDate: new Date().toISOString()
  //   }));

  //   setAttachments(prev => [...prev, ...newAttachments]);
  //   e.target.value = '';
  // }, []);

  // Helper function to format file size
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }, []);

  const handleFileUpload = useCallback((e, type) => {
    const files = Array.from(e.target.files);

    // Define size limits in bytes
    const SIZE_LIMITS = {
      image: 5 * 1024 * 1024,   // 5 MB
      audio: 5 * 1024 * 1024,   // 5 MB  
      file: 5 * 1024 * 1024     // 5 MB
    };

    const maxSize = SIZE_LIMITS[type];
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (file.size > maxSize) {
        invalidFiles.push({
          name: file.name,
          size: formatFileSize(file.size),
          maxSize: formatFileSize(maxSize)
        });
      } else {
        validFiles.push(file);
      }
    });

    // Show error for invalid files
    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles.map(f =>
        `${f.name} (${f.size}) exceeds the maximum size of ${f.maxSize}`
      );

      if (window.toast) {
        errorMessages.forEach(msg => window.toast.error(msg));
      } else {
        alert('File size errors:\n' + errorMessages.join('\n'));
      }
    }

    // Add valid files to attachments
    if (validFiles.length > 0) {
      const newAttachments = validFiles.map(file => ({
        id: Date.now() + Math.random(),
        name: file.name,
        type: type,
        size: file.size,
        file: file,
        url: URL.createObjectURL(file),
        uploadDate: new Date().toISOString()
      }));

      setAttachments(prev => [...prev, ...newAttachments]);

      if (window.toast) {
        window.toast.success(`${validFiles.length} file(s) added successfully!`);
      }
    }

    // Reset input
    e.target.value = '';
  }, [formatFileSize]);


  const removeAttachment = useCallback((id) => {
    setAttachments(prev => {
      const attachment = prev.find(att => att.id === id);
      if (attachment && attachment.url && attachment.url.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.url);
      }
      return prev.filter(attachment => attachment.id !== id);
    });
  }, []);

  // Audio recording functions
  const startRecording = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('MediaRecorder API is not supported in this browser');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      const options = MediaRecorder.isTypeSupported('audio/webm')
        ? { mimeType: 'audio/webm' }
        : {};

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });

        // Check audio file size (5MB limit)
        const MAX_AUDIO_SIZE = 5 * 1024 * 1024; // 5MB
        if (blob.size > MAX_AUDIO_SIZE) {
          stream.getTracks().forEach(track => track.stop());
          if (window.toast) {
            window.toast.error(`Recording too large (${formatFileSize(blob.size)}). Maximum size is ${formatFileSize(MAX_AUDIO_SIZE)}`);
          }
          setAudioBlob(null);
          return;
        }

        setAudioBlob(blob);

        const extension = mimeType.includes('webm') ? 'webm' : 'wav';
        const filename = `recording-${Date.now()}.${extension}`;
        const file = new File([blob], filename, { type: mimeType });

        const newAttachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          type: 'audio',
          size: file.size,
          file: file,
          url: URL.createObjectURL(blob),
          uploadDate: new Date().toISOString()
        };

        setAttachments(prev => [...prev, newAttachment]);
        stream.getTracks().forEach(track => track.stop());

        if (window.toast) {
          window.toast.success('Recording saved successfully!');
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
        if (window.toast) {
          window.toast.error('Recording failed: ' + event.error.message);
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);

      if (window.toast) {
        window.toast.success('Recording started');
      }

    } catch (error) {
      console.error('Error accessing microphone:', error);
      setIsRecording(false);

      let errorMessage = 'Could not access microphone. ';
      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow microphone access and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No microphone found.';
      } else {
        errorMessage += error.message;
      }

      if (window.toast) {
        window.toast.error(errorMessage);
      }
    }
  }, [formatFileSize]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);

        if (window.toast) {
          window.toast.success('Recording stopped');
        }
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    }
  }, [isRecording]);

  const playAudio = useCallback((url) => {
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
        if (window.toast) {
          window.toast.error('Could not play audio');
        }
      });
      setIsPlaying(true);

      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
          setDuration(audioRef.current.duration || 0);

          if (audioRef.current.ended) {
            setIsPlaying(false);
            setCurrentTime(0);
            clearInterval(progressIntervalRef.current);
          }
        }
      }, 100);
    }
  }, []);

  const pauseAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      clearInterval(progressIntervalRef.current);
    }
  }, []);

  // Utility functions
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }, []);

  const getFileIcon = useCallback((type) => {
    const iconClass = "w-6 h-6 text-white";
    switch (type) {
      case 'image':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>;
      case 'audio':
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 8c0-2.21-2.686-4-6-4S1 5.79 1 8s2.686 4 6 4 6-1.79 6-4z" />
        </svg>;
      default:
        return <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>;
    }
  }, []);

  const getEmployeeName = useCallback((employee) => {
    if (!employee) return 'Unknown Employee';
    return employee.teamMemberName ||
      `${employee.firstName || ''} ${employee.lastName || ''}`.trim() ||
      employee.name ||
      employee.fullName ||
      employee.displayName ||
      'Unknown Employee';
  }, []);

  const getDepartmentName = useCallback((department) => {
    if (!department) return 'Unknown Department';
    return department.name || department.departmentName || department.title || 'Unknown Department';
  }, []);

  // Form submission handler
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation.isValid) {
      setError(validation.error);
      setSnackbar({
        open: true,
        message: validation.error,
        type: 'error'
      });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const hasAttachments = attachments.length > 0;
      const companyId = await getCompanyId();
      const currentUserId = getCurrentUserId();

      if (!companyId) {
        throw new Error('Company ID is missing. Please log in again.');
      }
      if (!currentUserId) {
        throw new Error('User ID is missing. Please log in again.');
      }

      console.log('Submitting request with data:', {
        companyId,
        currentUserId,
        formData,
        hasAttachments
      });

      if (hasAttachments) {
        // Use FormData for requests with attachments
        const requestFormData = new FormData();

        // Add basic request data as individual fields
        requestFormData.append('companyId', companyId);
        requestFormData.append('requestFrom', currentUserId);
        requestFormData.append('department', formData.department);
        requestFormData.append('requestTo', formData.requestTo);
        requestFormData.append('subject', formData.subject);
        requestFormData.append('description', formData.description);
        requestFormData.append('requestType', 'employee_request');

        // Add attachments by type - same format as TaskForm
        attachments.forEach((attachment) => {
          if (attachment.file) {
            if (attachment.type === 'image') {
              requestFormData.append('images', attachment.file, attachment.name);
            } else if (attachment.type === 'audio') {
              requestFormData.append('audios', attachment.file, attachment.name);
            } else if (attachment.type === 'file') {
              requestFormData.append('files', attachment.file, attachment.name);
            }
          }
        });

        console.log(' FormData being sent with', attachments.length, 'file(s)');

        // Log FormData contents for debugging
        console.log('📋 FormData contents:');
        for (let pair of requestFormData.entries()) {
          if (pair[1] instanceof File) {
            console.log(`  ${pair[0]}: [File] ${pair[1].name} (${pair[1].size} bytes, ${pair[1].type})`);
          } else {
            console.log(`  ${pair[0]}: ${pair[1]}`);
          }
        }

        const response = await fetch(`${API_BASE_URL}/requests/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'x-auth-token': authToken
            // Note: Don't set Content-Type header - browser will set it automatically with boundary
          },
          credentials: 'include',
          body: requestFormData
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error('Response error:', errorData);
          throw new Error(`Failed to submit request with attachments (${response.status})`);
        }

        const result = await response.json();

        console.log('✅ Request submitted successfully!');
        console.log('📋 API Response:', JSON.stringify(result, null, 2));
        console.log('Ž Attachments in response:', result.attachments || result.data?.attachments || 'No attachments field');

        if (result.attachments && Array.isArray(result.attachments)) {
          console.log(`✅ ${result.attachments.length} attachment(s) saved:`);
          result.attachments.forEach((att, idx) => {
            console.log(`  ${idx + 1}. ${att.originalName || att.name} - ${att.url || 'No URL'}`);
          });
        } else if (result.data?.attachments) {
          console.log(`✅ ${result.data.attachments.length} attachment(s) in result.data:`);
          result.data.attachments.forEach((att, idx) => {
            console.log(`  ${idx + 1}. ${att.originalName || att.name} - ${att.url || 'No URL'}`);
          });
        } else {
          console.warn('⚠️ No attachments found in API response!');
        }

        setSnackbar({
          open: true,
          message: 'Request submitted successfully with attachments!',
          type: 'success'
        });

        if (onRequestSubmit) {
          onRequestSubmit(result);
        }

        // Close after delay to show success message
        setTimeout(() => {
          onClose();
        }, 2000);

      } else {
        // Use JSON for requests without attachments
        const requestData = {
          companyId: companyId,
          requestFrom: currentUserId,
          department: formData.department,
          requestTo: formData.requestTo,
          subject: formData.subject,
          description: formData.description,
          requestType: 'employee_request'
        };

        console.log('Sending JSON request data:', requestData);

        const response = await fetch(`${API_BASE_URL}/requests/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'x-auth-token': authToken
          },
          credentials: 'include',
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error('Response error:', errorData);
          throw new Error(`Failed to submit request (${response.status})`);
        }

        const result = await response.json();

        setSnackbar({
          open: true,
          message: 'Request submitted successfully!',
          type: 'success'
        });

        if (onRequestSubmit) {
          onRequestSubmit(result);
        }

        // Close after delay to show success message
        setTimeout(() => {
          onClose();
        }, 2000);
      }

    } catch (error) {
      console.error('Error submitting request:', error);
      setError(error.message);
      setSnackbar({
        open: true,
        message: 'Failed to submit request: ' + error.message,
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  }, [formData, attachments, validateForm, getCompanyId, getCurrentUserId, API_BASE_URL, authToken, onRequestSubmit, onClose]);

  // Reset form handler
  const resetForm = useCallback(() => {
    setFormData({
      department: '',
      requestTo: '',
      subject: '',
      description: ''
    });

    // Clean up attachments
    attachments.forEach(attachment => {
      if (attachment.url && attachment.url.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.url);
      }
    });
    setAttachments([]);
    setAudioBlob(null);
    setError(null);
  }, [attachments]);

  // Cleanup effect for audio
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      // Clean up object URLs to prevent memory leaks
      attachments.forEach(attachment => {
        if (attachment.url && attachment.url.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.url);
        }
      });
    };
  }, [attachments]);

  // Calculate if form is valid
  const isFormValid = validateForm().isValid;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 sm:from-green-600 sm:to-teal-600 px-4 sm:px-8 py-4 sm:py-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-0.5 sm:mb-1">Create Request</h2>
              <p className="text-xs sm:text-sm text-blue-100 sm:text-green-100">Submit a request to another employee</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            </div>
          )}



          {/* Department Selection */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Select Department *
            </label>

            {/* Desktop Native Select */}
            <div className="relative hidden sm:block">
              <select
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 appearance-none bg-white hover:border-green-300 transition-all duration-300 shadow-sm hover:shadow-md"
                required
                disabled={loading}
              >
                <option value="">Choose a department...</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>
                    {getDepartmentName(dept)}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                  <svg className="h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Mobile Custom Dropdown */}
            <div className="relative sm:hidden z-30">
              <button
                type="button"
                disabled={loading}
                onClick={() => setShowDeptDropdown(!showDeptDropdown)}
                className={`w-full flex items-center justify-between px-4 py-3 border-2 rounded-xl bg-white hover:border-green-300 transition-all duration-300 shadow-sm text-left
                  ${showDeptDropdown ? 'border-green-500 ring-4 ring-green-500/10' : 'border-gray-200'}`}
              >
                <span className={`text-sm ${formData.department ? 'text-gray-900 font-semibold' : 'text-gray-400 font-medium'}`}>
                  {formData.department
                    ? getDepartmentName(departments.find(d => d._id === formData.department)) || 'Choose a department...'
                    : 'Choose a department...'}
                </span>
                <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shrink-0 ml-2">
                  <svg className={`h-3 w-3 text-white transition-transform duration-200 ${showDeptDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </button>

              {showDeptDropdown && (
                <>
                  <div className="fixed inset-0 z-40 bg-black/5" onClick={() => setShowDeptDropdown(false)} />
                  <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto py-1">
                    <div
                      onClick={() => {
                        handleInputChange('department', '');
                        setShowDeptDropdown(false);
                      }}
                      className={`px-4 py-3 text-xs font-bold border-b border-gray-100 transition-colors cursor-pointer
                        ${!formData.department ? 'bg-green-50/50 text-green-700' : 'text-gray-400 hover:bg-slate-50'}`}
                    >
                      Choose a department...
                    </div>
                    {departments.map(dept => {
                      const isSelected = formData.department === dept._id;
                      return (
                        <div
                          key={dept._id}
                          onClick={() => {
                            handleInputChange('department', dept._id);
                            setShowDeptDropdown(false);
                          }}
                          className={`px-4 py-3 text-sm font-semibold transition-colors cursor-pointer flex items-center justify-between
                            ${isSelected ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-slate-50'}`}
                        >
                          <span>{getDepartmentName(dept)}</span>
                          {isSelected && (
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {loading && <p className="text-sm text-gray-500 mt-2">Loading departments...</p>}
          </div>

          {/* Employee Selection */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Select Employee *
            </label>

            {/* Desktop Native Select */}
            <div className="relative hidden sm:block">
              <select
                value={formData.requestTo}
                onChange={(e) => handleInputChange('requestTo', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white hover:border-blue-300 transition-all duration-300 shadow-sm hover:shadow-md"
                required
                disabled={!formData.department}
              >
                <option value="">
                  {!formData.department ? 'First select a department' : 'Choose an employee...'}
                </option>
                {filteredEmployees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {getEmployeeName(emp)}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                  <svg className="h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Mobile Custom Dropdown */}
            <div className="relative sm:hidden z-20">
              <button
                type="button"
                disabled={!formData.department}
                onClick={() => setShowEmpDropdown(!showEmpDropdown)}
                className={`w-full flex items-center justify-between px-4 py-3 border-2 rounded-xl bg-white hover:border-blue-300 transition-all duration-300 shadow-sm text-left
                  ${!formData.department ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}
                  ${showEmpDropdown ? 'border-blue-500 ring-4 ring-blue-500/10' : 'border-gray-200'}`}
              >
                <span className={`text-sm ${formData.requestTo ? 'text-gray-900 font-semibold' : 'text-gray-400 font-medium'}`}>
                  {!formData.department
                    ? 'First select a department'
                    : formData.requestTo
                      ? getEmployeeName(filteredEmployees.find(e => e._id === formData.requestTo)) || 'Choose an employee...'
                      : 'Choose an employee...'}
                </span>
                <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shrink-0 ml-2">
                  <svg className={`h-3 w-3 text-white transition-transform duration-200 ${showEmpDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </button>

              {showEmpDropdown && formData.department && (
                <>
                  <div className="fixed inset-0 z-40 bg-black/5" onClick={() => setShowEmpDropdown(false)} />
                  <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto py-1">
                    <div
                      onClick={() => {
                        handleInputChange('requestTo', '');
                        setShowEmpDropdown(false);
                      }}
                      className={`px-4 py-3 text-xs font-bold border-b border-gray-100 transition-colors cursor-pointer
                        ${!formData.requestTo ? 'bg-blue-50/50 text-blue-700' : 'text-gray-400 hover:bg-slate-50'}`}
                    >
                      Choose an employee...
                    </div>
                    {filteredEmployees.map(emp => {
                      const isSelected = formData.requestTo === emp._id;
                      return (
                        <div
                          key={emp._id}
                          onClick={() => {
                            handleInputChange('requestTo', emp._id);
                            setShowEmpDropdown(false);
                          }}
                          className={`px-4 py-3 text-sm font-semibold transition-colors cursor-pointer flex items-center justify-between
                            ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-slate-50'}`}
                        >
                          <span>{getEmployeeName(emp)}</span>
                          {isSelected && (
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Subject */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Subject *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter request subject..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 hover:border-purple-300 transition-all duration-300 shadow-sm hover:shadow-md"
              required
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{formData.subject.length}/200 characters</p>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your request in detail..."
              rows="5"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 hover:border-indigo-300 transition-all duration-300 shadow-sm hover:shadow-md resize-none"
              required
              maxLength={2000}
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description.length}/2000 characters</p>
          </div>

          {/* Attachments Section */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 border-2 border-dashed border-gray-200 shadow-sm mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 00-5.656-5.656l-6.586 6.586a6 6 0 108.486 8.486L20.5 13"></path>
              </svg>
              Attachments (Optional)
            </h3>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {/* Hidden file inputs */}
              <input
                type="file"
                ref={imageInputRef}
                onChange={(e) => handleFileUpload(e, 'image')}
                accept="image/*"
                multiple
                className="hidden"
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleFileUpload(e, 'file')}
                multiple
                className="hidden"
              />
              <input
                type="file"
                ref={audioInputRef}
                onChange={(e) => handleFileUpload(e, 'audio')}
                accept="audio/*"
                multiple
                className="hidden"
              />

              {/* Image Upload Button */}
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="flex flex-col items-center p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-xl transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-sm font-semibold text-blue-700">Images</span>
                <span className="text-xs text-blue-600 mt-1">Max 5 MB</span>
              </button>

              {/* Files Upload Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center p-4 bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 rounded-xl transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-sm font-semibold text-purple-700">Files</span>
                <span className="text-xs text-purple-600 mt-1">Max 5 MB</span>
              </button>

              {/* Audio Upload Button */}
              <button
                type="button"
                onClick={() => audioInputRef.current?.click()}
                className="flex flex-col items-center p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 rounded-xl transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <span className="text-sm font-semibold text-green-700">Audio Files</span>
                <span className="text-xs text-green-600 mt-1">Max 5 MB</span>
              </button>
            </div>


            {/* Attachment List */}
            {/* {attachments.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Attached Files ({attachments.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {attachments.map(attachment => (
                    <div key={attachment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center flex-1">
                        <div className="w-8 h-8 bg-gradient-to-br from-slate-400 to-slate-500 rounded-lg flex items-center justify-center mr-3">
                          {getFileIcon(attachment.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate text-sm">{attachment.name}</p>
                          <p className="text-xs text-slate-500">
                            {formatFileSize(attachment.size)} • {new Date(attachment.uploadDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {attachment.type === 'audio' && (
                        <div className="flex items-center mx-3 space-x-2">
                          <button
                            type="button"
                            onClick={() => isPlaying ? pauseAudio() : playAudio(attachment.url)}
                            className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center text-white transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isPlaying ? "M10 9v6m4-6v6" : "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"}></path>
                            </svg>
                          </button>
                          <div className="text-xs text-slate-500 min-w-0">
                            <div>{formatTime(currentTime)} / {formatTime(duration)}</div>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeAttachment(attachment.id)}
                        className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* Attachment List with Size Info on Cards */}
            {attachments.length > 0 && (
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Attached Files ({attachments.length})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {attachments.map(attachment => {
                    // Define size limits for each type (5MB)
                    const SIZE_LIMITS = {
                      image: { max: 5 * 1024 * 1024, label: '5 MB' },
                      audio: { max: 5 * 1024 * 1024, label: '5 MB' },
                      file: { max: 5 * 1024 * 1024, label: '5 MB' }
                    };

                    const typeLimit = SIZE_LIMITS[attachment.type] || SIZE_LIMITS.file;
                    const sizePercentage = (attachment.size / typeLimit.max) * 100;
                    const isOverSize = sizePercentage > 100;

                    // Get color scheme based on file type
                    const typeColors = {
                      image: {
                        bg: 'from-blue-400 to-blue-600',
                        light: 'bg-blue-50',
                        border: 'border-blue-200',
                        text: 'text-blue-700',
                        badge: 'bg-blue-100 text-blue-700'
                      },
                      audio: {
                        bg: 'from-green-400 to-green-600',
                        light: 'bg-green-50',
                        border: 'border-green-200',
                        text: 'text-green-700',
                        badge: 'bg-green-100 text-green-700'
                      },
                      file: {
                        bg: 'from-purple-400 to-purple-600',
                        light: 'bg-purple-50',
                        border: 'border-purple-200',
                        text: 'text-purple-700',
                        badge: 'bg-purple-100 text-purple-700'
                      }
                    };

                    const colors = typeColors[attachment.type] || typeColors.file;

                    return (
                      <div
                        key={attachment.id}
                        className={`${colors.light} ${colors.border} border-2 rounded-xl p-4 transition-all duration-300 hover:shadow-md`}
                      >
                        {/* Header with Icon and Type Badge */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className={`w-10 h-10 bg-gradient-to-br ${colors.bg} rounded-lg flex items-center justify-center mr-3 flex-shrink-0`}>
                              {getFileIcon(attachment.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-800 truncate text-sm">
                                {attachment.name}
                              </p>
                              <span className={`inline-block ${colors.badge} px-2 py-0.5 rounded-full text-xxs font-medium mt-1`}>
                                {attachment.type.toUpperCase()}
                              </span>
                            </div>
                          </div>

                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={() => removeAttachment(attachment.id)}
                            className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors ml-2 flex-shrink-0"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {/* File Size Information */}
                        <div className="space-y-2">
                          {/* Size Text */}
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-600">
                              <span className="font-medium">Size:</span> {formatFileSize(attachment.size)}
                            </span>
                            <span className={`font-medium ${isOverSize ? 'text-red-600' : colors.text}`}>
                              Limit: {typeLimit.label}
                            </span>
                          </div>

                          {/* Size Progress Bar */}
                          <div className="relative">
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${isOverSize
                                  ? 'bg-red-500'
                                  : sizePercentage > 80
                                    ? 'bg-yellow-500'
                                    : 'bg-gradient-to-r ' + colors.bg
                                  }`}
                                style={{ width: `${Math.min(sizePercentage, 100)}%` }}
                              />
                            </div>
                            <p className={`text-xxs mt-1 ${isOverSize ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                              {isOverSize ? '⚠️ Exceeds size limit!' : `${sizePercentage.toFixed(1)}% of max size`}
                            </p>
                          </div>

                          {/* Upload Date */}
                          <p className="text-xxs text-slate-400">
                            Added: {new Date(attachment.uploadDate).toLocaleString()}
                          </p>
                        </div>

                        {/* Audio Player Controls */}
                        {attachment.type === 'audio' && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => isPlaying ? pauseAudio() : playAudio(attachment.url)}
                                className={`w-9 h-9 ${colors.bg} bg-gradient-to-br hover:opacity-90 rounded-lg flex items-center justify-center text-white transition-all shadow-md`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d={isPlaying
                                      ? "M10 9v6m4-6v6"
                                      : "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                    }
                                  />
                                </svg>
                              </button>
                              <div className="flex-1 text-xs text-slate-600 font-medium">
                                {formatTime(currentTime)} / {formatTime(duration)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={resetForm}
              disabled={submitting}
              className="px-6 py-3 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset Form
            </button>
            <button
              type="submit"
              disabled={submitting || !isFormValid}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 sm:from-green-600 sm:to-teal-600 sm:hover:from-green-700 sm:hover:to-teal-700 text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Submitting Request...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Submit Request</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Hidden audio element for playback */}
        <audio ref={audioRef} style={{ display: 'none' }} />
      </div>

      {/* Snackbar Component */}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        duration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        position="top-right"
      />

      {/* Confirm Dialog Component */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={dialogLoading}
        showIcon={true}
      />
    </div>
  );
};

export default Request;


